<script lang="ts" setup>
import type { ResponseResult } from '~kui/types/formResult';

import { Heading } from '~ds/components/ui/typography';
import { defineField, defineFormConfig } from '~kui/components/KUI/Core/Form/util/form';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';

interface Props {
  initialFirstName?: string;
  initialLastName?: string;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  initialFirstName: '',
  initialLastName: '',
  loading: false,
});

const emit = defineEmits<{
  submit: [firstName: string, lastName: string];
}>();

const { t } = useI18n();

const formResult = ref<ResponseResult | null>(null);

const formConfig = defineFormConfig(
  {
    firstName: defineField({
      type: 'text',
      options: {
        label: t('general.formFields.firstName.label'),
        placeholder: t('general.formFields.firstName.placeholder'),
        autocomplete: 'given-name',
        required: true,
      },
      initialValue: props.initialFirstName,
      schema: z.string().min(1, t('general.formFields.firstName.required')).max(255, t('general.formFields.maxLength', { max: 255 })),
    }),
    lastName: defineField({
      type: 'text',
      options: {
        label: t('general.formFields.lastName.label'),
        placeholder: t('general.formFields.lastName.placeholder'),
        autocomplete: 'family-name',
        required: true,
      },
      initialValue: props.initialLastName,
      schema: z.string().min(1, t('general.formFields.lastName.required')).max(255, t('general.formFields.maxLength', { max: 255 })),
    }),
  },
  t('general.auth.submitButton.next'),
);

const handleSubmit = (value: z.infer<typeof formConfig.schema>) => {
  formResult.value = null;
  emit('submit', value.firstName, value.lastName);
};
</script>

<template>
  <Heading
    size="xl"
    as="h1"
    class="text-center mb-3"
  >
    {{ t('general.auth.niceMeetYou') }}
  </Heading>

  <div class="flex flex-col items-center">
    <KUICoreForm
      class="w-full"
      :form-config="formConfig"
      :form-result="formResult"
      :loading="loading"
      :allow-clean-submit="!!props.initialFirstName || !!props.initialLastName"
      submit-button-size="lg"
      @submit="handleSubmit"
    />
  </div>
</template>
